import { Input } from '@goaltree/ui/input'
import { cn } from '@goaltree/ui/lib/utils'
import { useForm } from '@tanstack/react-form'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

const titleSchema = z.string().min(1, 'Title cannot be empty')

type AddNodeRowProps = {
  label: string
  onAdd: (title: string) => Promise<unknown>
  className?: string
  // Opened from elsewhere (e.g. a header "+"): start as the input, and report closing.
  defaultOpen?: boolean
  onClose?: () => void
}

export function AddNodeRow({ label, onAdd, className, defaultOpen, onClose }: AddNodeRowProps) {
  const [open, setOpenState] = useState(!!defaultOpen)
  const setOpen = (next: boolean) => {
    setOpenState(next)
    if (!next) onClose?.()
  }

  const form = useForm({
    defaultValues: { title: '' },
    validators: { onSubmit: z.object({ title: titleSchema }) },
    onSubmit: async ({ value }) => {
      try {
        await onAdd(value.title)
        form.reset()
        setOpen(false)
      } catch {
        toast.error("Couldn't add — please try again.")
      }
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground/50 transition-colors hover:bg-muted hover:text-muted-foreground',
          className,
        )}
      >
        <Plus className="h-3 w-3" strokeWidth={2} />
        {label}
      </button>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="flex flex-col gap-1"
    >
      <form.Field name="title">
        {(field) => (
          <Input
            autoFocus
            placeholder={label}
            value={field.state.value}
            onChangeText={field.handleChange}
            onBlur={() => {
              field.handleBlur()
              if (!field.state.value.trim()) setOpen(false)
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape') {
                form.reset()
                setOpen(false)
              }
            }}
            className="h-7 border-0 bg-transparent px-0 text-sm shadow-none outline-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent md:text-sm"
          />
        )}
      </form.Field>
      <form.Subscribe selector={(s) => s.fieldMeta.title?.errors}>
        {(errors) =>
          errors && errors.length > 0 ? (
            <span className="text-xs text-destructive">{String(errors[0])}</span>
          ) : null
        }
      </form.Subscribe>
    </form>
  )
}
